import React from 'react';

export const DEFAULT_INSIGHT_CORNER_COLOR = '#f28c5c';

export const INSIGHT_COLOR_PALETTE = [
    '#ef4444', // red
    '#f97316', // orange
    '#eab308', // yellow
    '#60a5fa', // light blue
    '#1d4ed8', // dark blue
    '#4ade80', // light green
    '#ec4899', // magenta
    '#06b6d4', // cyan
    '#111827', // black
    '#8b5cf6', // purple
];

export type InsightFormState = {
    title: string;
    description: string;
    sourceUserName: string;
    sourceUserIdentifier: string;
    sourceUserIdentifierType: 'email' | 'linkedinId' | 'custom';
    sourceUrl: string;
    cornerColor: string;
};

export function emptyInsightForm(): InsightFormState {
    return {
        title: '',
        description: '',
        sourceUserName: '',
        sourceUserIdentifier: '',
        sourceUserIdentifierType: 'email',
        sourceUrl: '',
        cornerColor: DEFAULT_INSIGHT_CORNER_COLOR,
    };
}

type InsightFormProps = {
    value: InsightFormState;
    onChange: (key: keyof InsightFormState, value: string) => void;
    autoFocus?: boolean;
    existingUsers?: string[];
};

export default function InsightForm({ value, onChange, autoFocus = false, existingUsers }: InsightFormProps) {
    const userListId = 'insight-form-user-list';
    return (
        <>
            <label className='insight-field-label'>Title *</label>
            <input
                className='insight-field-input'
                value={value.title}
                onChange={(e) => onChange('title', e.target.value)}
                placeholder='Insight title'
                autoFocus={autoFocus}
            />

            <label className='insight-field-label'>Source user *</label>
            <input
                className='insight-field-input'
                value={value.sourceUserName}
                onChange={(e) => onChange('sourceUserName', e.target.value)}
                placeholder='Name of the person'
                list={existingUsers?.length ? userListId : undefined}
            />
            {existingUsers?.length ? (
                <datalist id={userListId}>
                    {existingUsers.map((name) => (
                        <option key={name} value={name} />
                    ))}
                </datalist>
            ) : null}

            <label className='insight-field-label'>
                User identifier
                <span className='insight-field-hint'> (email, LinkedIn)</span>
            </label>
            <div className='insight-identifier-row'>
                <input
                    className='insight-field-input insight-field-input--grow'
                    value={value.sourceUserIdentifier}
                    onChange={(e) => onChange('sourceUserIdentifier', e.target.value)}
                    placeholder='e.g. alice@example.com'
                />
                <select
                    className='insight-field-select'
                    value={value.sourceUserIdentifierType}
                    onChange={(e) => onChange('sourceUserIdentifierType', e.target.value)}
                >
                    <option value='email'>Email</option>
                    <option value='linkedinId'>LinkedIn ID</option>
                    <option value='custom'>Custom</option>
                </select>
            </div>

            <label className='insight-field-label'>Source URL</label>
            <input
                className='insight-field-input'
                value={value.sourceUrl}
                onChange={(e) => onChange('sourceUrl', e.target.value)}
                placeholder='https://'
                type='url'
            />

            <label className='insight-field-label'>Description *</label>
            <textarea
                className='insight-field-textarea'
                value={value.description}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder='What was the insight?'
                rows={3}
            />

            <div className='insight-color-row'>
                <label className='insight-field-label'>Corner color</label>
                <div className='insight-color-palette'>
                    {INSIGHT_COLOR_PALETTE.map((color) => (
                        <button
                            key={color}
                            type='button'
                            className={`insight-color-swatch${value.cornerColor === color ? ' insight-color-swatch--selected' : ''}`}
                            style={{ '--swatch-color': color } as React.CSSProperties}
                            title={color}
                            onClick={() => onChange('cornerColor', color)}
                        />
                    ))}
                </div>
            </div>
        </>
    );
}
